interface MapDim {
  height: number
  width: number
}

export type MapType = 'roadmap' | 'satellite' | 'hybrid' | 'terrain'
export const MAP_TYPE_DEFAULT: MapType = 'roadmap'
export const MAP_ROADMAP_DROPDOWN_TYPES: MapType[] = ['roadmap', 'terrain']
export const MAP_SATELLITE_DROPDOWN_TYPES: MapType[] = ['satellite', 'hybrid']

const WORLD_DIM = { height: 256, width: 256 }
const ZOOM_MAX = 15

function latRadOf(lat: number): number {
  const sin = Math.sin((lat * Math.PI) / 180)
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
}

function calcZoom(mapPx: number, worldPx: number, fraction: number): number {
  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
}

export function getZoomForBounds(
  { n, w, s, e }: Bounds,
  mapDim?: MapDim,
): number {
  if (!n || !w || !s || !e) return ZOOM_MAX
  if (!mapDim) return 1

  const latFraction = (latRadOf(n) - latRadOf(s)) / Math.PI

  const lngDiff = e - w
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = calcZoom(mapDim.height, WORLD_DIM.height, latFraction)
  const lngZoom = calcZoom(mapDim.width, WORLD_DIM.width, lngFraction)

  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}

/**
 * Returns empty bounds with zero values.
 */
export function getBoundsEmpty(): Bounds {
  return { s: 0, w: 0, e: 0, n: 0 }
}

/**
 * Returns the approximate bounds of Europe.
 */
export function getBoundsDefault(): Bounds {
  return { s: 27, w: -17, n: 71, e: 35 }
}

export function getBoundsFromViewport({
  south,
  north,
  east,
  west,
}: Viewport): Bounds {
  return {
    s: south ?? 0,
    w: west ?? 0,
    n: north ?? 0,
    e: east ?? 0,
  }
}

export function getBoundsFromCoordBounds({
  top_left: nw,
  bottom_right: se,
}: CoordBounds): Bounds {
  return {
    s: se?.lat ?? 0,
    w: nw?.lon ?? 0,
    n: nw?.lat ?? 0,
    e: se?.lon ?? 0,
  }
}

export function getBoundsFromCoords({ lat, lng }: Coord): Bounds {
  return {
    s: lat! - 0.01,
    w: lng! - 0.01,
    n: lat! + 0.01,
    e: lng! + 0.01,
  }
}

export function getMapBounds(map: google.maps.Map): Bounds {
  const ne = map.getBounds()?.getNorthEast()
  const sw = map.getBounds()?.getSouthWest()

  return {
    s: sw?.lat() ?? 0,
    w: sw?.lng() ?? 0,
    n: ne?.lat() ?? 0,
    e: ne?.lng() ?? 0,
  }
}

export function getMapDim(
  element: HTMLElement | null | undefined,
  settings?: { mapBoundsPadding?: number },
): MapDim | undefined {
  if (!element) return undefined

  const padding = settings?.mapBoundsPadding ?? 0

  return {
    height: element.offsetHeight - padding * 2,
    width: element.offsetWidth - padding * 2,
  }
}

export function isViewportValid({
  south,
  north,
  east,
  west,
}: Viewport): boolean {
  return south !== north && east !== west
}

export function isBoundsEqual(a: Bounds, b: Bounds, tolerance = 0): boolean {
  return (['s', 'w', 'n', 'e'] as const)
    .map((prop) => Math.abs(a[prop] - b[prop]))
    .every((delta) => delta <= tolerance)
}

export function toLatLng({ lat, lon }: { lat: number; lon: number }) {
  return { lat, lng: lon }
}

export function toLatLngBounds({ s, w, n, e }: Bounds) {
  return { east: e, north: n, south: s, west: w }
}

export interface OverlayViewOptions {
  position?: google.maps.LatLng | google.maps.LatLngLiteral
  map?: any
  element?: HTMLElement
  pane?: keyof google.maps.MapPanes
  zIndex?: number
}

export interface OverlayViewCallbacks {
  onAdd?: () => void
  draw?: () => void
  onRemove?: () => void
}

export type OverlayViewClass = new (
  opts: OverlayViewOptions,
  callbacks?: OverlayViewCallbacks,
) => {
  setOptions(newOptions: OverlayViewOptions): void
  setCallbacks(newCallbacks: OverlayViewCallbacks): void
} & google.maps.OverlayView

export function createOverlayViewClass(
  api: typeof google.maps,
): OverlayViewClass {
  return class OverlayView extends api.OverlayView {
    private element?: HTMLElement
    private opts: OverlayViewOptions
    private callbacks: OverlayViewCallbacks
    private renderDelayTimeout = 50

    constructor(
      opts: OverlayViewOptions,
      callbacks: OverlayViewCallbacks = {},
    ) {
      super()

      this.opts = opts
      this.callbacks = callbacks
      this.element = opts.element

      if (this.opts.map) {
        this.setMap(this.opts.map)
      }
    }

    getElement() {
      return this.element
    }

    override onAdd() {
      if (this.callbacks.onAdd) {
        return this.callbacks.onAdd()
      }

      const panes = this.getPanes()

      if (!this.element || !panes) return

      const targetPane: keyof google.maps.MapPanes =
        this.opts.pane || 'overlayLayer'
      ;(panes as any)[targetPane].appendChild(this.element)

      const { position } = this.opts

      if (!position) return

      setTimeout(() => {
        const childElement = this.element?.firstElementChild as HTMLElement

        if (!childElement) return

        const map = this.getMap() as google.maps.Map
        const { height: mapHeight } = map.getDiv().getBoundingClientRect()
        const { height } = childElement.getBoundingClientRect()

        const padding = 15
        const shift = height - Math.floor(mapHeight / 2) + 20

        map.panTo(position)

        if (shift > 0) {
          map.panBy(0, -(shift + padding))
        }
      }, this.renderDelayTimeout)
    }

    override draw() {
      if (this.callbacks.draw) {
        this.callbacks.draw()
      } else if (this.element && this.opts.position) {
        // Default behavior: position element at specified location
        const overlayProjection = this.getProjection()
        const position =
          this.opts.position instanceof api.LatLng
            ? this.opts.position
            : new api.LatLng(this.opts.position)
        const point = overlayProjection?.fromLatLngToDivPixel(position)

        if (point) {
          this.element.style.position = 'absolute'
          this.element.style.left = `${point.x}px`
          this.element.style.top = `${point.y}px`
          this.element.style.transform = 'translate(-50%, -50%)'

          if (this.opts.zIndex !== undefined) {
            this.element.style.zIndex = this.opts.zIndex.toString()
          }
        }
      }
    }

    override onRemove() {
      if (this.callbacks.onRemove) {
        this.callbacks.onRemove()
      } else if (this.element) {
        // Default behavior: remove element from DOM
        this.element.remove()
      }
    }

    setOptions(opts: OverlayViewOptions) {
      this.opts = { ...this.opts, ...opts }
      if (opts.element) {
        this.element = opts.element
      }
      this.draw()
    }

    setCallbacks(callbacks: OverlayViewCallbacks) {
      this.callbacks = { ...this.callbacks, ...callbacks }
    }
  }
}

export function getTargetType(
  requestedType: MapType,
  currentType: MapType,
): MapType {
  if (requestedType === 'terrain') {
    return currentType === 'roadmap' ? 'terrain' : 'roadmap'
  }
  if (requestedType === 'hybrid') {
    return currentType === 'satellite' ? 'hybrid' : 'satellite'
  }
  return requestedType
}

export function mapTypeToGoogleMapTypeId(): Record<
  MapType,
  google.maps.MapTypeId
> {
  return {
    roadmap: google.maps.MapTypeId.ROADMAP,
    satellite: google.maps.MapTypeId.SATELLITE,
    hybrid: google.maps.MapTypeId.HYBRID,
    terrain: google.maps.MapTypeId.TERRAIN,
  }
}

export function mapGoogleMapTypeIdToMapType(): Record<
  google.maps.MapTypeId | string,
  MapType
> {
  const mapTypeToGoogle = mapTypeToGoogleMapTypeId()

  return Object.fromEntries(
    Object.entries(mapTypeToGoogle).map(([key, value]) => [value, key]),
  ) as Record<google.maps.MapTypeId | string, MapType>
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace google.maps {
    export const CustomOverlayView: OverlayViewClass
  }
}
